import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchPropertyComments, addPropertyComment, markCommentRead } from '../../Redux/Actions/actions';
import style from './PropertyComments.module.css';

const TIPO_COLORS = {
  comentario: '#666',
  aprobacion: '#28a745',
  rechazo: '#dc3545',
  revision: '#fd7e14',
  aprobacion_documentacion: '#0d9488',
  rechazo_documentacion: '#9b1c1c',
};

const TIPO_LABELS = {
  comentario: 'Comentario',
  aprobacion: 'Aprobación',
  rechazo: 'Rechazo',
  revision: 'Revisión',
  aprobacion_documentacion: 'Doc. aprobada',
  rechazo_documentacion: 'Doc. rechazada',
};

const normalizeUserType = (value) => {
  const v = String(value || '').trim().toUpperCase();
  if (v === 'MARTILLER') return 'MARTILLER';
  if (v === 'SELLER' || v === 'VENDEDOR') return 'SELLER';
  return 'SELLER';
};

const resolveCommentAuthor = (comment) => {
  const explicitType = normalizeUserType(comment?.autorTipo);
  const inferredType = comment?.autorMartiller ? 'MARTILLER' : 'SELLER';
  const tipo = comment?.autorTipo ? explicitType : inferredType;

  const sellerObj = comment?.autor || comment?.seller;
  const martillerObj = comment?.autorMartiller;
  const person = tipo === 'MARTILLER' ? martillerObj : sellerObj;

  const name = [person?.name, person?.last_name].filter(Boolean).join(' ').trim() || 'Usuario';
  const photo = person?.photo || person?.img || null;

  return { name, photo, tipo };
};

const PropertyComments = ({ propertyId }) => {
  const dispatch = useDispatch();
  const propertyComments = useSelector((state) => state.propertyComments);
  const versioningLoading = useSelector((state) => state.versioningLoading);
  const userId = useSelector((state) => state.userId);
  const user = useSelector((state) => state.user);
  const userType = useSelector((state) => state.userType);
  const [mensaje, setMensaje] = useState('');
  const [tipo, setTipo] = useState('comentario');
  const [submitting, setSubmitting] = useState(false);

  const currentUserId = user?.user?.id || userId || null;
  const currentUserType = normalizeUserType(user?.user?.type || user?.type || userType);

  useEffect(() => {
    if (propertyId) {
      dispatch(fetchPropertyComments(propertyId));
    }
  }, [dispatch, propertyId]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!mensaje.trim()) return;
    setSubmitting(true);
    try {
      await dispatch(addPropertyComment(propertyId, {
        autorTipo: currentUserType,
        autorId: currentUserId,
        ...(currentUserType === 'SELLER' ? { sellerId: currentUserId } : {}),
        tipo,
        mensaje: mensaje.trim(),
      }));
      setMensaje('');
      dispatch(fetchPropertyComments(propertyId));
    } catch {
      alert('Error al agregar comentario.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleMarkRead = (commentId) => {
    dispatch(markCommentRead(commentId));
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleString('es-AR', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  };

  return (
    <div className={style.container}>
      <h3 className={style.title}>Comentarios</h3>

      <form onSubmit={handleSubmit} className={style.form}>
        <div className={style.formRow}>
          <select
            value={tipo}
            onChange={(e) => setTipo(e.target.value)}
            className={style.tipoSelect}
          >
            <option value="comentario">Comentario</option>
            <option value="revision">Revisión</option>
          </select>
          <textarea
            value={mensaje}
            onChange={(e) => setMensaje(e.target.value)}
            placeholder="Escribir un comentario..."
            className={style.textarea}
            rows={2}
            required
          />
          <button type="submit" className={style.submitBtn} disabled={submitting || !mensaje.trim()}>
            {submitting ? 'Enviando...' : 'Enviar'}
          </button>
        </div>
      </form>

      {versioningLoading && propertyComments.length === 0 && (
        <div className={style.loading}>Cargando comentarios...</div>
      )}

      {!versioningLoading && propertyComments.length === 0 && (
        <div className={style.empty}>No hay comentarios aún.</div>
      )}

      <div className={style.commentsList}>
        {propertyComments.map((comment) => (
          <div
            key={comment.id}
            className={style.commentItem}
            style={{ borderLeftColor: TIPO_COLORS[comment.tipo] || '#666' }}
          >
            {(() => {
              const author = resolveCommentAuthor(comment);
              return (
                <div className={style.commentHeader}>
                  <div className={style.commentAuthorWrap}>
                    {author.photo ? (
                      <img src={author.photo} alt={author.name} className={style.commentAvatar} />
                    ) : (
                      <div className={style.commentAvatar}>{author.name.charAt(0).toUpperCase()}</div>
                    )}
                    <strong className={style.commentAuthor}>{author.name}</strong>
                    <span className={style.commentType}>({author.tipo})</span>
                  </div>
              <span
                className={style.tipoBadge}
                style={{ backgroundColor: TIPO_COLORS[comment.tipo] || '#666' }}
              >
                {TIPO_LABELS[comment.tipo] || comment.tipo}
              </span>
              {!comment.leido && (
                <button
                  className={style.unreadBadge}
                  onClick={() => handleMarkRead(comment.id)}
                  title="Marcar como leído"
                >
                  ● No leído
                </button>
              )}
                </div>
              );
            })()}
            <p className={style.commentMessage}>{comment.mensaje}</p>
            <small className={style.commentDate}>{formatDate(comment.createdAt)}</small>
          </div>
        ))}
      </div>
    </div>
  );
};

export default PropertyComments;
